/*
 * Vitrine — 3D scene. Owns the live viewport, the offscreen thumbnail renderer,
 * model and sole loaders, and the spin-on-load animation. No DOM beyond the canvas
 * it's handed at init.
 *
 * Public surface:
 *   const v = VitrineViewer.create({ canvas, container });
 *   v.loadModel(url, cb, opts)  — load a shoe GLB, replacing any current model
 *                                 (opts.buffer: parse preloaded bytes, no network).
 *   v.loadSole(url, cb)         — load a sole GLB, attaching it under the model.
 *   v.spinOnLoad(durationMs)    — kick off a 360° one-shot spin animation.
 *   v.captureThumbnail({ model, colorway, soleUrl }) — offscreen single-shoe render.
 *   v.getScene() / v.getModelScene() — for the painter / external classification.
 */
(function (w) {
  'use strict';

  if (typeof THREE === 'undefined') { console.error('[vitrine] THREE missing.'); return; }

  var DEG = Math.PI / 180;
  var Painter = w.VitrinePainter;

  function create(opts) {
    var canvas    = opts.canvas;
    var container = opts.container;
    if (!canvas || !container) throw new Error('[vitrine] viewer requires canvas + container');

    // ── Renderer + scene ────────────────────────────────────────────────────
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // opts.staticShadows — the lights never move, so when the shoes don't either
    // (product page: no carousel) the shadow pass only needs re-running when the
    // scene changes. Halves per-frame draw calls; markShadowsDirty() below.
    if (opts.staticShadows) renderer.shadowMap.autoUpdate = false;
    function markShadowsDirty() { renderer.shadowMap.needsUpdate = true; }

    // Don't render while the canvas is scrolled out of view or the tab is
    // hidden — the scene is ~1–3k draw calls per frame, which otherwise keeps
    // the GPU busy and makes the rest of the page scroll janky.
    var onScreen = true;
    if ('IntersectionObserver' in w) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].isIntersecting;
      }).observe(canvas);
    }

    // WebGL context loss (common under memory pressure on mobile, e.g. after
    // re-cloning models). preventDefault is what allows the context to be
    // RESTORED — without it the canvas is gone for good and every later frame
    // throws. While lost we pause rendering; on restore three re-uploads textures
    // on the next render, so the shoe comes back instead of staying black.
    var contextLost = false;
    var renderErrorLogged = false;
    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      contextLost = true;
      console.warn('[vitrine] WebGL context lost — pausing render until restored');
    }, false);
    canvas.addEventListener('webglcontextrestored', function () {
      contextLost = false;
      renderErrorLogged = false;
      console.warn('[vitrine] WebGL context restored');
    }, false);

    var scene = new THREE.Scene();
    scene.background = new THREE.Color('#ECEAE5');
    scene.fog = new THREE.Fog('#ECEAE5', 4.8, 10);

    var camera = new THREE.PerspectiveCamera(40, 1, 0.01, 100);
    camera.position.set(2.4, 0.55, 0);

    var controls = new THREE.OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance   = 0.5;
    controls.maxDistance   = 5;
    controls.enablePan     = false;
    controls.target.set(0, 0, 0);
    controls.update();
    // Touching the model cancels the intro spin / focus glide so the user takes over instantly.
    controls.addEventListener('start', function () { spinAnim = null; focusAnim = null; });

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    var keyLight = new THREE.DirectionalLight(0xfff8f0, 1.8);
    keyLight.position.set(2, 4, 3);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(1024, 1024);
    keyLight.shadow.camera.near   = 0.1;
    keyLight.shadow.camera.far    = 15;
    keyLight.shadow.camera.left   = -2;
    keyLight.shadow.camera.right  =  2;
    keyLight.shadow.camera.top    =  2;
    keyLight.shadow.camera.bottom = -2;
    keyLight.shadow.bias   = -0.001;
    keyLight.shadow.radius = 6;
    scene.add(keyLight);

    var fillLight = new THREE.DirectionalLight(0xf0f4ff, 0.36);
    fillLight.position.set(-3, 1, -2);
    scene.add(fillLight);

    var rimLight = new THREE.DirectionalLight(0xffffff, 0.48);
    rimLight.position.set(0, 1.8, -3);
    scene.add(rimLight);

    // Generative backdrop — a slow colour haze behind the shoe, tinted to match
    // the build. The flat scene.background still shows through where it's subtle.
    var backdrop = w.VitrineBackdrop ? w.VitrineBackdrop.create({ base: '#ECEAE5' }) : null;
    if (backdrop) scene.add(backdrop.object);

    // ── State for the live scene ────────────────────────────────────────────
    // Only the shoe model loads from the network here; soles are reused from the
    // preloaded set (loadSole clones a preloaded gltf), so no sole loader.
    var loader = new THREE.GLTFLoader();
    // Shoe GLBs are Draco-compressed — wire the shared decoder.
    if (w.__cfDracoLoader && loader.setDRACOLoader) loader.setDRACOLoader(w.__cfDracoLoader);

    var currentWrapper      = null;
    var currentModelScene   = null;
    var currentSoleScene    = null;
    var currentSoleSideMeshes = []; // Side_1 meshes within the sole, painted with welt color
    var currentLiningSnapshot = null;
    var mirrorPair          = null; // the mirrored LEFT shoe (display partner) — see rebuildMirror
    var mirrorToOriginal    = null; // Map<cloneMesh, originalMesh> so a click on the partner picks the hero shoe's part
    var shoeCenterOffset    = new THREE.Vector3();
    var spinAnim            = null; // { startTheta, deltaTheta, duration, elapsed }
    var focusAnim           = null; // { fromPos, toPos, fromTarget, toTarget, elapsed, duration }
    // Camera tour (product page "Play"): glide to a shot, turn slowly, next.
    var tour                = null; // { shots, i, phase: 'travel'|'hold', t }
    // Top inset (px): controls float over the top of the canvas, so the shoe is
    // shrunk and pushed down to sit centred in the clear area beneath them.
    var inset               = { cur: 0, target: 0, lcur: 0, ltarget: 0, bcur: 0, btarget: 0 }; // top, left, bottom

    // Render multiplexing. 'single' draws the live model scene below; 'carousel'
    // hands the renderer to the Design-step coverflow carousel (its own scene +
    // camera, but the SAME renderer/canvas — one GL context).
    var mode     = 'single';
    var carousel = null;

    // Load token — every loadModel call bumps it. A stale GLTF callback (from a
    // prior call still in flight) checks its token and bails before adding anything
    // to the scene. Prevents two shoes overlapping when the user navigates rapidly
    // between Design and Review. (Sole attach is synchronous, so it needs no token.)
    var modelToken = 0;

    // ── Sizing ──────────────────────────────────────────────────────────────
    function resize() {
      var w = container.clientWidth, h = container.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      applyInset();
      if (carousel) carousel.resize(w, h);
    }

    function applyInset() {
      var w = container.clientWidth, h = container.clientHeight;
      if (!w || !h) return;
      var t = Math.max(0, Math.min(inset.cur, h * 0.5));
      var l = Math.max(0, Math.min(inset.lcur, w * 0.6));
      var b = Math.max(0, Math.min(inset.bcur, h * 0.6 - t));
      // Portrait frames (phones) are narrow — pull back so the pair still fits across.
      // (opts.fitPortrait — product page only; the Vitrine keeps its framing.)
      var portrait = opts.fitPortrait ? Math.min(1, Math.max(0.7, (w / h) / 0.9)) : 1;
      camera.zoom = Math.max(0.4, Math.min((h - t - b) / h, (w - l) / w)) * portrait;
      if (t > 0.5 || l > 0.5 || b > 0.5) camera.setViewOffset(w, h, -l / 2, (b - t) / 2, w, h);
      else camera.clearViewOffset();
      camera.updateProjectionMatrix();
    }

    // ── Camera tour ─────────────────────────────────────────────────────────
    // shots: [{ pos:[x,y,z], target:[x,y,z], travel?:ms, hold?:ms, spin?:deg/s }]
    function tourNext() {
      if (!tour) return;
      tour.i = (tour.i + 1) % tour.shots.length;
      var sh = tour.shots[tour.i];
      focusTo(new THREE.Vector3(sh.pos[0], sh.pos[1], sh.pos[2]),
              new THREE.Vector3(sh.target[0], sh.target[1], sh.target[2]),
              sh.travel || 2200);
      tour.phase = 'travel';
      tour.t = 0;
    }
    function startTour(shots, o) {
      if (!shots || !shots.length) return;
      // Resume with the shot after the last one shown, not from the top
      // (o.restart: begin at the first shot — the Vitrine beta's part orbit).
      var from = (o && o.restart) ? -1
        : (tour && tour.shots === shots) ? tour.i : (startTour.lastI != null ? startTour.lastI : -1);
      tour = { shots: shots, i: from, phase: 'travel', t: 0, noResume: !!(o && o.restart) };
      tourNext();
    }
    function stopTour() {
      if (tour && !tour.noResume) startTour.lastI = tour.i;
      tour = null;
      focusAnim = null;
      spinAnim = null;
    }
    function orbitStep(rad) {
      var off = camera.position.clone().sub(controls.target);
      off.applyAxisAngle(new THREE.Vector3(0, 1, 0), rad);
      camera.position.copy(controls.target).add(off);
    }
    window.addEventListener('resize', resize);
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(resize).observe(container);
    resize();

    // ── Camera helpers ──────────────────────────────────────────────────────
    function getCameraTheta() { return Math.atan2(camera.position.x, camera.position.z); }
    function setCameraTheta(theta) {
      var r = Math.sqrt(camera.position.x * camera.position.x + camera.position.z * camera.position.z);
      camera.position.x = r * Math.sin(theta);
      camera.position.z = r * Math.cos(theta);
    }

    // ── Spin-on-load: one full revolution, easing out, then stop ────────────
    function spinOnLoad(durationMs) {
      spinAnim = {
        startTheta: getCameraTheta(),
        deltaTheta: Math.PI * 2,
        duration:   durationMs || 2400,
        elapsed:    0
      };
    }

    // Named camera framings for the build steps, plus a free "focus on these
    // meshes" mode for the parts editor. Positions are in the single-model scene
    // (model centred at origin, ~1.4 units on its longest dimension).
    var VIEW_PRESETS = {
      'default': { pos: [2.4, 0.55, 0.0],  target: [0, 0.0,  0] },
      'colors':  { pos: [2.4, 0.55, 0.0],  target: [0, 0.0,  0] },
      'sole':    { pos: [1.7, -0.5, 1.7],  target: [0, -0.25, 0] }, // low, looking up at welt/sole
      'lining':  { pos: [1.3, 1.7, 1.2],   target: [0, 0.1,  0] },  // high-front, into the topline
      'hero':    { pos: [2.2, 0.7, 1.4],   target: [0, 0.0,  0] },
      // Parts editor sits in tight — a close three-quarter so the shoe fills the
      // frame and stays put while the user rotates it by hand.
      'parts':   { pos: [1.5, 0.45, 0.95], target: [0, 0.0,  0] }
    };

    function focusTo(toPos, toTarget, durationMs) {
      spinAnim = null;
      focusAnim = {
        fromPos:    camera.position.clone(),
        toPos:      toPos.clone(),
        fromTarget: controls.target.clone(),
        toTarget:   toTarget.clone(),
        elapsed:    0,
        duration:   durationMs || 700
      };
    }

    function focusView(preset, opts) {
      var p = VIEW_PRESETS[preset] || VIEW_PRESETS['default'];
      focusTo(new THREE.Vector3(p.pos[0], p.pos[1], p.pos[2]),
              new THREE.Vector3(p.target[0], p.target[1], p.target[2]),
              opts && opts.duration);
      if (preset === 'hero' && opts && opts.spin !== false) {
        // Hero view also kicks off the signature one-shot spin once it lands.
        setTimeout(function () { spinOnLoad(2400); }, 720);
      }
    }

    // Frame a set of meshes (a selected part): tween the target to their bounding
    // box centre and pull the camera in along its current viewing direction.
    function focusOnObjects(meshes) {
      if (!meshes || !meshes.length) return;
      var box = new THREE.Box3();
      var tmp = new THREE.Box3();
      var found = false;
      meshes.forEach(function (m) {
        if (!m || !m.isMesh) return;
        tmp.setFromObject(m);
        if (tmp.isEmpty()) return;
        found ? box.union(tmp) : box.copy(tmp);
        found = true;
      });
      if (!found) return;
      var center = box.getCenter(new THREE.Vector3());
      var size   = box.getSize(new THREE.Vector3());
      var maxDim = Math.max(size.x, size.y, size.z) || 0.3;
      var fovRad = camera.fov * DEG;
      var dist   = clamp((maxDim / 2) / Math.tan(fovRad / 2) * 2.2,
                         controls.minDistance + 0.1, controls.maxDistance);
      var dir = camera.position.clone().sub(controls.target);
      if (dir.lengthSq() < 1e-6) dir.set(2.4, 0.55, 0);
      dir.normalize();
      focusTo(center.clone().add(dir.multiplyScalar(dist)), center, 700);
    }

    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

    var lastFrame = performance.now();
    (function animate() {
      requestAnimationFrame(animate);
      // Skip work while the GL context is gone, but keep the loop alive so it
      // resumes automatically once 'webglcontextrestored' fires.
      if (contextLost) { lastFrame = performance.now(); return; }
      if (!onScreen || document.hidden) { lastFrame = performance.now(); return; }
      var now = performance.now();
      var dt  = Math.min((now - lastFrame) / 1000, 0.1);
      lastFrame = now;

      // One try/catch around the frame: a single bad frame logs ONCE and the
      // loop keeps going, instead of throwing every frame ("error keeps
      // repeating") and potentially taking the page down with it.
      try {
        if (mode === 'carousel') {
          // No carousel yet (Vitrine still loading) → nothing to draw.
          if (!carousel) return;
          carousel.update(dt);
          renderer.render(carousel.getScene(), carousel.getCamera());
          return;
        }

        if (Math.abs(inset.target - inset.cur) > 0.5 || Math.abs(inset.ltarget - inset.lcur) > 0.5 || Math.abs(inset.btarget - inset.bcur) > 0.5) {
          inset.cur  += (inset.target  - inset.cur)  * Math.min(1, dt * 7);
          inset.lcur += (inset.ltarget - inset.lcur) * Math.min(1, dt * 7);
          inset.bcur += (inset.btarget - inset.bcur) * Math.min(1, dt * 7);
          applyInset();
        } else if (inset.cur !== inset.target || inset.lcur !== inset.ltarget || inset.bcur !== inset.btarget) {
          inset.cur = inset.target; inset.lcur = inset.ltarget; inset.bcur = inset.btarget; applyInset();
        }

        if (tour && !focusAnim) {
          var sh = tour.shots[tour.i];
          if (tour.phase === 'travel') { tour.phase = 'hold'; tour.t = 0; }
          tour.t += dt * 1000;
          // Ease the turn in and out so the move between shots isn't a jolt.
          var hold = sh.hold || 4500, u = tour.t / hold;
          var env = Math.sin(Math.PI * Math.min(u, 1));
          orbitStep((sh.spin != null ? sh.spin : 6) * DEG * dt * (0.4 + 0.6 * env));
          if (tour.t >= hold) tourNext();
        }

        if (focusAnim) {
          focusAnim.elapsed += dt * 1000;
          var ft   = Math.min(focusAnim.elapsed / focusAnim.duration, 1);
          // Tour glides ease in AND out; every other focus keeps its snappy ease-out.
          var fe   = tour ? (ft < 0.5 ? 4 * ft * ft * ft : 1 - Math.pow(-2 * ft + 2, 3) / 2)
                          : 1 - Math.pow(1 - ft, 3); // ease-out cubic
          camera.position.lerpVectors(focusAnim.fromPos, focusAnim.toPos, fe);
          controls.target.lerpVectors(focusAnim.fromTarget, focusAnim.toTarget, fe);
          if (ft >= 1) focusAnim = null;
        } else if (spinAnim) {
          spinAnim.elapsed += dt * 1000;
          var t    = Math.min(spinAnim.elapsed / spinAnim.duration, 1);
          var ease = 1 - Math.pow(1 - t, 3); // ease-out cubic
          setCameraTheta(spinAnim.startTheta + spinAnim.deltaTheta * ease);
          if (t >= 1) spinAnim = null;
        }
        controls.update();
        if (backdrop) backdrop.update(dt);
        renderer.render(scene, camera);
      } catch (err) {
        if (!renderErrorLogged) { console.error('[vitrine] render loop error:', err); renderErrorLogged = true; }
      }
    })();

    // ── Mirrored partner (the other foot) ───────────────────────────────────
    // Slides 2+ (the single-model steps) show the right shoe you configure PLUS a
    // mirrored LEFT shoe, posed as a display pair: set off to one side and a touch
    // back, tipped so the heel lifts, and yawed inward toward the hero shoe.
    //
    // Real GLB axes (verified from the meshes — the toe↔heel run is the long one):
    //   X = length  (toe ↔ heel)   — longest dimension
    //   Y = up
    //   Z = width   (lateral ↔ medial)
    // So a LEFT shoe is the right shoe reflected across the WIDTH axis (scale.z),
    // which keeps the toe pointing the same way; the pair separates along Z.
    var PAIR = {
      side:     -0.60,  // world-Z (width) separation — sits the pair side by side, clear of each other
      back:    -0.08,  // small world-X (length) stagger so the toes don't line up dead even
      lift:     0.23,   // extra world-Y raise of the whole partner
      heelDeg:  -15,    // tip about the width axis to elevate the heel (flip sign if the toe lifts instead)
      inDeg:    8     // yaw about up so the toe turns inward toward the hero shoe
    };

    function clearMirror() {
      // Clone meshes share geometry + materials with currentWrapper — never dispose
      // them here; just detach and let the GC collect the cloned Object3D wrappers.
      if (mirrorPair) { scene.remove(mirrorPair); mirrorPair = null; }
      mirrorToOriginal = null;
      markShadowsDirty();
    }

    // Rebuild the mirrored partner from the CURRENT right shoe (+ its sole). Cloning
    // captures the materials as they stand right now, so callers must invoke this
    // AFTER each repaint (the painter replaces material instances, so a stale clone
    // would keep the old colours). reflowFloor() is wired to this for that reason.
    function rebuildMirror() {
      clearMirror();
      if (!currentWrapper) return;
      currentWrapper.updateWorldMatrix(true, true);
      var pair = currentWrapper.clone(true);
      // clone(true) preserves child order, so a parallel traversal pairs each
      // cloned node with its source 1:1 — lets a click on the partner resolve to
      // the hero shoe's mesh (groups are matched by mesh identity, not the clone).
      mirrorToOriginal = new Map();
      var origNodes = [];
      currentWrapper.traverse(function (o) { origNodes.push(o); });
      var ni = 0;
      pair.traverse(function (o) {
        var orig = origNodes[ni++];
        if (o.isMesh && orig && orig.isMesh) mirrorToOriginal.set(o, orig);
      });
      pair.scale.z *= -1;                       // mirror across the WIDTH axis → left shoe, same facing
      var pivot = new THREE.Group();
      pivot.add(pair);
      pivot.position.set(PAIR.back, PAIR.lift, PAIR.side);
      // pivot.position.set(0,0,0);
      // pivot.position.set(0,[PAIR.back, PAIR.lift, PAIR.side]);
      pivot.rotation.z = PAIR.heelDeg * DEG;    // elevate the heel
      pivot.rotation.y = PAIR.inDeg  * DEG;     // rotate inwards
      mirrorPair = pivot;
      scene.add(mirrorPair);
      markShadowsDirty();
    }

    // ── Model loading ───────────────────────────────────────────────────────
    function clearWrapper() {
      clearMirror();
      if (currentWrapper) {
        scene.remove(currentWrapper);
        currentWrapper = null;
      }
      currentModelScene = null;
      currentSoleScene = null;
      currentSoleSideMeshes = [];
      currentLiningSnapshot = null;
    }

    // loadModel(url, cb, opts)
    //   opts.buffer — the GLB bytes already fetched at preload: parse those
    //                 into a fresh scene instead of hitting the network again.
    function loadModel(url, cb, opts) {
      opts = opts || {};
      var token = ++modelToken;
      clearWrapper();

      function attach(gltf) {
        if (token !== modelToken) return; // a newer loadModel call superseded this one
        var modelScene = gltf.scene;
        // Shoe id for per-shoe fit tuning — from the GLB name (shoe-<id>[-f].glb).
        if (!modelScene.userData.cfShoeId) {
          var idm = /shoe-([a-z0-9]+?)(?:-f)?\.glb/i.exec(String(url || ''));
          if (idm) modelScene.userData.cfShoeId = idm[1].toLowerCase();
        }
        Painter.fixSceneDepth(modelScene);
        modelScene.traverse(function (obj) { if (obj.isMesh) obj.castShadow = true; });

        var box    = new THREE.Box3().setFromObject(modelScene);
        var center = box.getCenter(new THREE.Vector3());
        var size   = box.getSize(new THREE.Vector3());
        modelScene.position.set(-center.x, -center.y, -center.z);
        shoeCenterOffset.copy(modelScene.position);

        var maxDim  = Math.max(size.x, size.y, size.z) || 1;
        var wrapper = new THREE.Group();
        wrapper.scale.setScalar(1.4 / maxDim);
        wrapper.add(modelScene);
        scene.add(wrapper);

        currentWrapper    = wrapper;
        currentModelScene = modelScene;
        currentLiningSnapshot = Painter.snapshotInsideMeshes(modelScene);
        markShadowsDirty();

        if (cb) cb(null, modelScene, gltf);
      }
      function fail(err) {
        if (token !== modelToken) return;
        console.error('[vitrine] model load failed:', err);
        if (cb) cb(err);
      }

      if (opts.buffer) {
        // extractUrlBase keeps resource resolution identical to load(url).
        loader.parse(opts.buffer, THREE.LoaderUtils.extractUrlBase(url), attach, fail);
        return;
      }
      loader.load(url, attach, undefined, fail);
    }

    // Attach a sole from an ALREADY-PRELOADED gltf — no network request. The sole
    // was fetched once at preload; cloning here (same as the carousel + thumbnails
    // do) gives the live scene its own copy so painting the welt never mutates the
    // shared original. Passing a falsy gltf just clears the current sole.
    function loadSole(soleGltf, cb) {
      if (currentSoleScene && currentWrapper) currentWrapper.remove(currentSoleScene);
      currentSoleScene = null;
      currentSoleSideMeshes = [];
      if (!soleGltf || !soleGltf.scene || !currentWrapper) { if (cb) cb(); return; }

      var soleScene = soleGltf.scene.clone(true);
      Painter.fixSceneDepth(soleScene);
      soleScene.traverse(function (obj) { if (obj.isMesh) obj.castShadow = true; });
      soleScene.position.copy(shoeCenterOffset);

      currentSoleScene = soleScene;
      currentWrapper.add(soleScene);
      // Welt down to 2mm above this sole, sole flush under it (vitrine-core).
      // The live upper is fitted again on every sole swap, so put the welt back
      // on its own pre-fit geometry first — see resetWeltFit().
      if (Painter.fitSoleToUpper && currentModelScene) {
        resetWeltFit(currentModelScene);
        Painter.fitSoleToUpper(currentModelScene, soleScene, soleFitOptions(currentModelScene));
      }

      soleScene.traverse(function (obj) {
        if (obj.isMesh && obj.name === 'Side_1') currentSoleSideMeshes.push(obj);
      });
      markShadowsDirty();

      if (cb) cb(null, soleScene);
    }

    // fitSoleToUpper reshapes the welt by giving each welt mesh a COPY of its
    // geometry. On a later fit of the same upper it reuses that copy and writes
    // the new shape into it, and the copy is also what its per-sole cache hands
    // back — so after cloud → ground the cached "cloud" entry holds the ground
    // shape, and cloud → ground → cloud left the welt stuck at ground. Restoring
    // each welt mesh's first (unfitted) geometry before every fit makes the
    // painter clone a fresh copy each time, so cached shapes are never
    // overwritten. Kept here (theme-owned) so a vitrine-core sync can't drop it.
    function resetWeltFit(modelScene) {
      var welt = Painter.weltMeshes ? Painter.weltMeshes(modelScene) : [];
      welt.forEach(function (m) {
        if (m.__cfPreFitGeom) m.geometry = m.__cfPreFitGeom;
        else m.__cfPreFitGeom = m.geometry;
      });
    }
    // Per-shoe tuning for the fit (window.cfSoleFitOptions, catalog-patch).
    // The shoe id rides on the scene (tagged at load; clones copy userData).
    function soleFitOptions(modelScene) {
      var id = modelScene && modelScene.userData && modelScene.userData.cfShoeId;
      return (id && window.cfSoleFitOptions) ? window.cfSoleFitOptions(id) : undefined;
    }

    // ── Offscreen thumbnail rendering ──────────────────────────────────────
    // A separate renderer + scene so the live viewport never flickers during preview generation.
    var offCanvas = document.createElement('canvas');
    offCanvas.width = offCanvas.height = 768;
    // Created on first thumbnail capture, not up front — the product page never
    // captures, and a second WebGL context costs startup time and GPU memory.
    var offRenderer = null;
    function getOffRenderer() {
      if (offRenderer) return offRenderer;
      offRenderer = new THREE.WebGLRenderer({ canvas: offCanvas, antialias: true, preserveDrawingBuffer: true });
      offRenderer.setPixelRatio(1);
      offRenderer.outputEncoding = THREE.sRGBEncoding;
      offRenderer.setSize(768, 768);
      return offRenderer;
    }

    // Crop a finished offscreen render tight to the shoe, at the tiles' 3:2
    // shape, so the shoe fills the picture instead of floating in a square of
    // backdrop. Anything that isn't the backdrop colour counts as shoe; the
    // crop is padded a little and any area past the render is backdrop.
    var THUMB_W = 480, THUMB_H = 320, THUMB_PAD = 0.035;
    var cropCanvas = null, readCanvas = null;
    function croppedThumbURL() {
      var W = offCanvas.width, H = offCanvas.height;
      try {
        if (!readCanvas) { readCanvas = document.createElement('canvas'); readCanvas.width = W; readCanvas.height = H; }
        var rc = readCanvas.getContext('2d', { willReadFrequently: true });
        rc.drawImage(offCanvas, 0, 0);
        var px = rc.getImageData(0, 0, W, H).data;
        var bg = [px[0], px[1], px[2]];   // backdrop, read from the corner
        var x0 = W, y0 = H, x1 = -1, y1 = -1;
        for (var y = 0; y < H; y++) {
          for (var x = 0; x < W; x++) {
            var i = (y * W + x) * 4;
            if (Math.abs(px[i] - bg[0]) + Math.abs(px[i + 1] - bg[1]) + Math.abs(px[i + 2] - bg[2]) > 24) {
              if (x < x0) x0 = x; if (x > x1) x1 = x;
              if (y < y0) y0 = y; if (y > y1) y1 = y;
            }
          }
        }
        if (x1 < 0) return offCanvas.toDataURL('image/jpeg', 0.87);
        var bw = x1 - x0 + 1, bh = y1 - y0 + 1, pad = Math.max(bw, bh) * THUMB_PAD;
        var A = THUMB_W / THUMB_H;
        var cw = Math.max(bw + 2 * pad, (bh + 2 * pad) * A);
        var cx = x0 + bw / 2, cy = y0 + bh / 2;
        if (!cropCanvas) { cropCanvas = document.createElement('canvas'); cropCanvas.width = THUMB_W; cropCanvas.height = THUMB_H; }
        var cc = cropCanvas.getContext('2d');
        cc.fillStyle = 'rgb(' + bg.join(',') + ')';
        cc.fillRect(0, 0, THUMB_W, THUMB_H);
        cc.imageSmoothingQuality = 'high';
        var s = THUMB_W / cw;
        cc.drawImage(offCanvas, THUMB_W / 2 - cx * s, THUMB_H / 2 - cy * s, W * s, H * s);
        return cropCanvas.toDataURL('image/jpeg', 0.88);
      } catch (e) {
        try { return offCanvas.toDataURL('image/jpeg', 0.87); } catch (e2) { return null; }
      }
    }

    var offScene = new THREE.Scene();
    offScene.background = new THREE.Color('#ECEAE5');
    offScene.add(new THREE.AmbientLight(0xffffff, 0.6));
    var offKey  = new THREE.DirectionalLight(0xfff8f0, 1.8); offKey.position.set(2, 4, 3);   offScene.add(offKey);
    var offFill = new THREE.DirectionalLight(0xf0f4ff, 0.4); offFill.position.set(-3, 1, -2); offScene.add(offFill);
    var offCam  = new THREE.PerspectiveCamera(40, 1, 0.01, 100);
    offCam.position.set(1.05, 0.28, 0);
    offCam.lookAt(0, 0, 0);

    // Build a one-shoe wrapper for offscreen rendering. Optionally attaches a sole.
    function buildOffWrapper(gltf, soleGltf) {
      var modelScene = gltf.scene.clone(true);
      Painter.fixSceneDepth(modelScene);
      var box    = new THREE.Box3().setFromObject(modelScene);
      var center = box.getCenter(new THREE.Vector3());
      var size   = box.getSize(new THREE.Vector3());
      modelScene.position.set(-center.x, -center.y, -center.z);

      var soleSide = [];
      var holder = new THREE.Group();
      holder.add(modelScene);
      if (soleGltf) {
        var soleScene = soleGltf.scene.clone(true);
        Painter.fixSceneDepth(soleScene);
        soleScene.position.set(-center.x, -center.y, -center.z);
        soleScene.traverse(function (obj) { if (obj.isMesh && obj.name === 'Side_1') soleSide.push(obj); });
        holder.add(soleScene);
        if (Painter.fitSoleToUpper) Painter.fitSoleToUpper(modelScene, soleScene, soleFitOptions(modelScene));
      }

      var wrapper = new THREE.Group();
      var maxDim  = Math.max(size.x, size.y, size.z) || 1;
      wrapper.scale.setScalar(1.05 / maxDim);
      wrapper.add(holder);
      return { wrapper: wrapper, scene: modelScene, soleSide: soleSide };
    }

    function fitOffCamera(wrapper) {
      wrapper.updateWorldMatrix(true, true);
      var box    = new THREE.Box3().setFromObject(wrapper);
      var center = box.getCenter(new THREE.Vector3());
      var size   = box.getSize(new THREE.Vector3());
      var maxDim = Math.max(size.x, size.y, size.z);
      var fovRad = offCam.fov * Math.PI / 180;
      var dist   = (maxDim / 2) / Math.tan(fovRad / 2) * 1.25;
      offCam.position.set(center.x, center.y, center.z + dist);
      offCam.lookAt(center.x, center.y, center.z);
      offCam.updateProjectionMatrix();
    }

    // The five — and only five — orientations a part thumbnail may be shot from.
    // Each vector is the direction FROM the shoe centre TOWARD the camera, with a
    // little built-in elevation so the framing reads as a product shot rather than
    // a flat orthographic side. Axes (matching the GLB/live scene): X = lateral
    // (outside ↔ inside), Y = up, Z = length (toe ↔ heel).
    var PART_VIEW_DIRS = [
      { id: 'outside', v: new THREE.Vector3( 1.0, 0.30,  0.35) },
      { id: 'inside',  v: new THREE.Vector3(-1.0, 0.30,  0.35) },
      { id: 'top',     v: new THREE.Vector3( 0.20, 1.0,  0.20) },
      { id: 'toe',     v: new THREE.Vector3( 0.30, 0.30,  1.0) },
      { id: 'heel',    v: new THREE.Vector3( 0.30, 0.30, -1.0) }
    ];
    PART_VIEW_DIRS.forEach(function (d) { d.v.normalize(); });
    function partViewDir(id) {
      for (var i = 0; i < PART_VIEW_DIRS.length; i++) if (PART_VIEW_DIRS[i].id === id) return PART_VIEW_DIRS[i].v;
      return PART_VIEW_DIRS[0].v;
    }

    // Choose the best of the five angles for a part: the candidate whose direction
    // most closely matches where the part sits relative to the whole shoe (so an
    // outside panel is shot from outside, a toe cap from the toe, etc.). Laces are
    // always shot from the top — that's where they read. Parts with no clear offset
    // (centred panels) fall back to the outside three-quarter view.
    function pickPartViewDir(fullCenter, partCenter, modelSize, isLaces) {
      if (isLaces) return partViewDir('top');
      var offset = partCenter.clone().sub(fullCenter);
      var span   = Math.max(modelSize.x, modelSize.y, modelSize.z) || 1;
      if (offset.length() < span * 0.04) return partViewDir('outside');
      offset.normalize();
      var best = PART_VIEW_DIRS[0], bestDot = -Infinity;
      PART_VIEW_DIRS.forEach(function (d) {
        var dot = d.v.dot(offset);
        if (dot > bestDot) { bestDot = dot; best = d; }
      });
      return best.v;
    }

    // Frame the whole shoe (so the part shows in context) from a chosen direction.
    function fitOffCameraDir(wrapper, dir) {
      wrapper.updateWorldMatrix(true, true);
      var box    = new THREE.Box3().setFromObject(wrapper);
      var center = box.getCenter(new THREE.Vector3());
      var size   = box.getSize(new THREE.Vector3());
      var maxDim = Math.max(size.x, size.y, size.z);
      var fovRad = offCam.fov * Math.PI / 180;
      var dist   = (maxDim / 2) / Math.tan(fovRad / 2) * 1.25;
      offCam.position.copy(center).add(dir.clone().multiplyScalar(dist));
      offCam.lookAt(center.x, center.y, center.z);
      offCam.updateProjectionMatrix();
    }

    // Paint an offscreen scene with a colorway and grab a thumbnail data URL.
    // gltf is the pre-loaded model; soleGltf is the pre-loaded sole (optional).
    // paintFn(groups, colorway) optionally replaces the default colorway paint so
    // the caller can apply a shoe-specific split (e.g. desert) identically to the
    // live render — keeping the thumbnail and the on-screen shoe in agreement.
    // One offscreen copy per shoe + sole, framed once and repainted for each
    // colourway — a run of colourway thumbnails used to clone, fit and frame
    // the shoe twice per thumbnail. Every colourway paints every group (body,
    // sole/welt, laces, stitching, sole edge), so nothing carries over.
    var thumbBuild = null; // { gltf, sole, built }
    function captureThumbnail(gltf, soleGltf, colorway, paintFn) {
      if (!gltf) return null;
      if (!thumbBuild || thumbBuild.gltf !== gltf || thumbBuild.sole !== (soleGltf || null)) {
        var b = buildOffWrapper(gltf, soleGltf);
        offScene.add(b.wrapper);
        fitOffCamera(b.wrapper);
        offScene.remove(b.wrapper);
        thumbBuild = { gltf: gltf, sole: soleGltf || null, built: b, cam: offCam.position.clone(), quat: offCam.quaternion.clone() };
      } else {
        // capturePartThumbnail moves the shared camera — put it back.
        offCam.position.copy(thumbBuild.cam);
        offCam.quaternion.copy(thumbBuild.quat);
        offCam.updateProjectionMatrix();
      }
      var built = thumbBuild.built;
      if (colorway) {
        var groups = Painter.groupByTexture(built.scene);
        if (paintFn) paintFn(groups, colorway);
        else Painter.applyColorway(groups, colorway);
        if (colorway.sole && built.soleSide.length) {
          Painter.paintMeshes(built.soleSide, colorway.sole.color);
        }
      }
      offScene.add(built.wrapper);
      getOffRenderer().render(offScene, offCam);
      var url = null;
      url = croppedThumbURL();
      offScene.remove(built.wrapper);
      return url;
    }

    // Capture a thumbnail of one part as a silhouette: the part renders in black,
    // every other mesh in light off-white. The camera is shot from whichever of
    // the five fixed angles (pickPartViewDir) best faces the part — so each card
    // shows the part it's actually about, instead of a single side view where
    // half the parts are hidden round the back.
    //
    // Implementation: clone the current wrapper into the offscreen scene, swap
    // every material for a basic color material, render, dispose, return URL.
    // Converted to linear so they show as written through the sRGB output
    // (unconverted, both washed out: the "white" silhouette vanished into the
    // #ECEAE5 backdrop and the black part read mid-grey).
    var PART_BLACK = new THREE.Color('#1A1A1A').convertSRGBToLinear();
    var PART_WHITE = new THREE.Color('#CFC7BA').convertSRGBToLinear();
    function capturePartThumbnail(group) {
      if (!currentWrapper || !group || !group.meshes || !group.meshes.length) return null;

      var partNames = {};
      group.meshes.forEach(function (m) { if (m.name) partNames[m.name] = true; });
      // Welt/sole groups extend onto the separately-loaded sole GLB's side meshes,
      // so the thumbnail darkens those too — otherwise the part the user sees
      // painted in 3D wouldn't match the part shown in the strip.
      if (group.isSole) {
        currentSoleSideMeshes.forEach(function (m) { if (m.name) partNames[m.name] = true; });
      }

      var clone = currentWrapper.clone(true);
      var createdMats = [];
      var partBox = new THREE.Box3();
      var tmpBox  = new THREE.Box3();
      var partFound = false;
      clone.traverse(function (obj) {
        if (!obj.isMesh) return;
        var isPart = !!partNames[obj.name];
        var mat = new THREE.MeshBasicMaterial({ color: isPart ? PART_BLACK : PART_WHITE });
        createdMats.push(mat);
        obj.material = mat;
        if (isPart) {
          tmpBox.setFromObject(obj);
          if (!tmpBox.isEmpty()) { partFound ? partBox.union(tmpBox) : partBox.copy(tmpBox); partFound = true; }
        }
      });

      offScene.add(clone);
      // Pick the best of the five fixed angles for this part (laces → top),
      // then frame the whole shoe from that direction.
      clone.updateWorldMatrix(true, true);
      var fullBox  = new THREE.Box3().setFromObject(clone);
      var fullCtr  = fullBox.getCenter(new THREE.Vector3());
      var fullSize = fullBox.getSize(new THREE.Vector3());
      var isLaces  = !!group.isLaces || group.meshes.some(function (m) { return Painter && Painter.isLaceName && Painter.isLaceName(m.name); });
      var partCtr  = partFound ? partBox.getCenter(new THREE.Vector3()) : fullCtr.clone();
      var dir      = pickPartViewDir(fullCtr, partCtr, fullSize, isLaces);
      fitOffCameraDir(clone, dir);
      getOffRenderer().render(offScene, offCam);
      var url = null;
      url = croppedThumbURL();
      offScene.remove(clone);
      createdMats.forEach(function (m) { m.dispose(); });

      return url;
    }

    // Briefly tint a group's materials so the user sees which part is selected.
    // Uses emissive so the colour painted on the part is preserved.
    var flashTimer = null;
    function flashPart(group) {
      if (!group || !group.meshes.length) return;
      // clearTimeout before restore(): restore() nulls flashTimer, so the old
      // order threw on every overlapping flash and killed the Sole-step pulse.
      if (flashTimer) { clearTimeout(flashTimer.id); flashTimer.restore(); }
      // Welt/sole flash also covers the sole sides on the separately-loaded sole.
      var meshes = group.isSole
        ? group.meshes.concat(currentSoleSideMeshes)
        : group.meshes;
      var saved = [];
      meshes.forEach(function (m) {
        var mats = Array.isArray(m.material) ? m.material : [m.material];
        mats.forEach(function (mat) {
          if (mat && mat.emissive) {
            saved.push({ mat: mat, color: mat.emissive.clone(), intensity: mat.emissiveIntensity });
            mat.emissive.set('#FFFFFF');
            mat.emissiveIntensity = (mat.emissiveIntensity || 1) * 0.5;
          }
        });
      });
      var restore = function () {
        saved.forEach(function (s) { s.mat.emissive.copy(s.color); s.mat.emissiveIntensity = s.intensity; });
        flashTimer = null;
      };
      flashTimer = { id: setTimeout(restore, 220), restore: restore };
    }

    // Restore an in-progress flash immediately (used before navigating away or
    // repainting — otherwise the white emissive can get cloned into the new
    // materials and "stick" on the part).
    function cancelFlash() {
      if (!flashTimer) return;
      clearTimeout(flashTimer.id);
      flashTimer.restore();
    }

    // Raycast a click on the canvas to find which mesh was hit. Returns the mesh or
    // null. The caller looks the mesh up in its group list.
    function pickMeshAtClick(event) {
      if (!currentWrapper) return null;
      var rect = canvas.getBoundingClientRect();
      var ndc = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      var raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(ndc, camera);
      // Raycast both shoes; nearest hit wins. A hit on the mirrored partner maps
      // back to the matching mesh on the hero shoe, so selecting a part on the
      // second shoe behaves exactly like selecting it on the first.
      var targets = mirrorPair ? [currentWrapper, mirrorPair] : [currentWrapper];
      var hits = raycaster.intersectObjects(targets, true);
      for (var i = 0; i < hits.length; i++) {
        var obj = hits[i].object;
        if (obj && obj.isMesh && obj.visible) {
          return (mirrorToOriginal && mirrorToOriginal.has(obj)) ? mirrorToOriginal.get(obj) : obj;
        }
      }
      return null;
    }

    return {
      getScene:        function () { return scene; },
      getModelScene:   function () { return currentModelScene; },
      getSoleScene:    function () { return currentSoleScene; },
      getSoleSideMeshes: function () { return currentSoleSideMeshes; },
      // Every mesh of the separately-loaded sole (the Sole step flashes it whole).
      getSoleMeshes: function () {
        var out = [];
        if (currentSoleScene) currentSoleScene.traverse(function (o) { if (o.isMesh) out.push(o); });
        return out;
      },
      getLiningSnapshot: function () { return currentLiningSnapshot; },
      loadModel:       loadModel,
      loadSole:        loadSole,
      spinOnLoad:      spinOnLoad,
      // Continuous turntable (booth mode). OrbitControls applies it in update(),
      // which the animate loop already calls every frame.
      setAutoRotate:   function (on, speed) {
        controls.autoRotate = !!on;
        if (speed) controls.autoRotateSpeed = speed;
      },
      getRenderer:     function () { return renderer; },
      setCarousel:     function (c) { carousel = c; },
      setMode:         function (m) {
        mode = (m === 'carousel') ? 'carousel' : 'single';
        controls.enabled = (mode === 'single');
        if (carousel) carousel.setActive(mode === 'carousel');
        resize();
      },
      focusView:       focusView,
      focusOnObjects:  focusOnObjects,
      // Glide to an exact camera position + target and stay there (ends any
      // camera tour). The Vitrine uses it to face a picked part when paused.
      flyTo: function (pos, target, ms) {
        stopTour();
        focusTo(new THREE.Vector3(pos[0], pos[1], pos[2]), new THREE.Vector3(target[0], target[1], target[2]), ms || 900);
      },
      startTour:       startTour,
      stopTour:        stopTour,
      // Pixels at the top of the canvas covered by floating controls; the shoe
      // re-centres in what's left (eased in the render loop).
      setTopInset:     function (px) { inset.target = Math.max(0, px || 0); },
      // Same for controls down the left side (Vitrine beta on wide screens).
      setLeftInset:    function (px) { inset.ltarget = Math.max(0, px || 0); },
      setBottomInset:  function (px) { inset.btarget = Math.max(0, px || 0); },
      // Pointer input on/off without touching carousel mode.
      setInteractive:  function (on) { controls.enabled = !!on && mode === 'single'; },
      setBackdropColors: function (cw) {
        if (!backdrop || !cw) return;
        backdrop.setColors(
          cw.body1 && cw.body1.color,
          cw.body2 && cw.body2.color,
          cw.sole  && cw.sole.color
        );
      },
      setBackdropHex: function (a, b, c) { if (backdrop) backdrop.setColors(a, b, c); },
      captureThumbnail:   captureThumbnail,
      capturePartThumbnail: capturePartThumbnail,
      pickMeshAtClick:    pickMeshAtClick,
      flashPart:          flashPart,
      cancelFlash:        cancelFlash,
      // Rebuild the mirrored partner. Called at the end of every repaint (where the
      // shoe's materials have just settled) and after a sole-only swap.
      reflowFloor: rebuildMirror
    };
  }

  w.VitrineViewer = { create: create };
})(window);
